<template>
    <div :id="modalID" class="modal fade" data-bs-backdrop="static" data-bs-keyboard="true" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered" role="document" style="min-width: 1100px">
            <div class="modal-content">
                <div class="modal-header d-flex align-items-center">
                    <div style="font-size: 15px; font-weight: 500; line-height: 21px; color: #00203f">
                        {{ $t('views.inventory.modals.expenses.title') }}
                    </div>
                    <button type="button" class="close-button btn btn-link" @click="closeModal"><i class="bi bi-x-lg" style="color: #677788"></i></button>
                </div>
                <div class="modal-body">
                    <table class="table table-borderless table-thead-bordered modal-table">
                        <thead class="thead-light">
                            <tr>
                                <th>#</th>
                                <th>{{ $t('views.inventory.modals.expenses.amount.title') }}</th>
                                <th>{{ $t('views.inventory.modals.expenses.price.title') }} <InfoTooltip :content="$t('views.inventory.modals.expenses.price.tooltip')" class="text-black text-none"></InfoTooltip></th>
                                <th>{{ $t('views.inventory.modals.expenses.end_date.title') }}</th>
                                <th>{{ $t('views.inventory.modals.expenses.platform.title') }}</th>
                                <th>{{ $t('views.inventory.modals.expenses.orderId.title') }}</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(item, index) in expenses" :key="item.id">
                                <td style="max-width: 46px">{{ index + 1 }}</td>
                                <td style="min-width: 150px">
                                    <div class="input-group">
                                        <input v-model="item.amount" type="number" :placeholder="$t('views.inventory.modals.expenses.amount.placeholder')" class="form-control custom-form-control" />
                                        <span class="input-group-text custom-form-control" style="background-color: #fff !important">#</span>
                                    </div>
                                </td>
                                <td style="min-width: 225px">
                                    <div class="d-flex">
                                        <div class="input-group">
                                            <CurrencyInput v-model="item.price" class="form-control custom-form-control" :placeholder="$t('views.inventory.modals.expenses.price.placeholder')" style="max-width: 125px" />
                                            <span class="input-group-text custom-form-control" style="background-color: #fff !important; font-size: 12px"
                                                >{{ item.currency ? item.currency : '$' }}
                                                <InfoTooltip v-if="item.source" :content="$t('views.inventory.modals.expenses.price.isFromSourceTooltip')" class="text-black text-none" style="font-size: 10px; padding-left: 2px"></InfoTooltip
                                            ></span>
                                        </div>
                                        <button
                                            class="btn btn-link text-secondary dropdown-toggle d-flex"
                                            type="button"
                                            data-bs-toggle="dropdown"
                                            aria-expanded="false"
                                            style="background-color: var(--bs-gray-200); font-size: 11px; gap: 5px"
                                            data-bs-dropdown-animation
                                        >
                                            <template v-if="item.source">
                                                <img class="svg-flag" :src="require(`@/assets/svg-flag/${item.source}.svg`)" width="16" height="16" alt="" /> <span>{{ item.source }}</span>
                                            </template>
                                            <template v-else> {{ $t('common.select') }}</template>
                                        </button>
                                        <div class="dropdown-menu p-0" style="width: 85px; min-width: 85px; max-height: 200px; overflow: auto">
                                            <button type="button" v-for="short in getShortFromMarket" :key="short" class="dropdown-item with-border d-flex align-items-center" style="gap: 5px" @click="changeTargetCountry(short, index)">
                                                <img class="svg-flag" :src="require(`@/assets/svg-flag/${short}.svg`)" width="16" height="16" alt="" /> <span>{{ short }}</span>
                                            </button>
                                        </div>
                                    </div>
                                </td>
                                <td style="min-width: 150px">
                                    <VueDatePicker
                                        v-model="item.end_date"
                                        locale="tr"
                                        auto-apply
                                        format="yyyy-MM-dd"
                                        :action-row="{ showPreview: false }"
                                        :enable-time-picker="false"
                                        :clearable="false"
                                        style="max-width: 125px !important"
                                        :placeholder="$t('views.inventory.modals.expenses.end_date.placeholder')"
                                    >
                                        <template #input-icon> <i class="bi bi-calendar-week dp__input_icon dp__input_icons" style="margin-top: -4px"></i> </template
                                    ></VueDatePicker>
                                </td>
                                <td>
                                    <select v-model="item.buying_platform" class="form-select form-select-sm">
                                        <option :value="null" selected disabled>{{ $t('views.inventory.modals.expenses.platform.placeholder') }}</option>
                                        <option v-for="item in platforms" :key="item" :value="item" :selected="item == item.buying_platform">{{ item }}</option>
                                    </select>
                                </td>
                                <td>
                                    <input
                                        type="text"
                                        :value="item.buying_order_id"
                                        class="form-control custom-form-control"
                                        :placeholder="$t('views.inventory.modals.expenses.orderId.placeholder')"
                                        style="max-width: 100px"
                                        @input="handleOrderId($event, index)"
                                    />
                                </td>
                                <td style="max-width: 46px">
                                    <Popper hover arrow>
                                        <template #content><div v-html="$t('views.inventory.modals.expenses.remove')"></div></template><button class="btn btn-link p-0" @click="remove(index)"><i class="fa-solid fa-trash text-secondary"></i></button
                                    ></Popper>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div class="modal-footer justify-content-between">
                    <span class="text-muted" style="font-size: 12px" v-html="$t('views.inventory.modals.expenses.asinText', { asin })"></span>
                    <div class="d-flex align-items-center" style="gap: 10px">
                        <button class="btn btn-outline-secondary border btn-sm" :disabled="isNewRowButtonDisabled || isSubmitButtonLoading" @click="add">{{ $t('views.inventory.modals.expenses.newRow') }}</button>
                        <button class="btn btn-primary btn-sm d-flex align-items-center" :disabled="isNewRowButtonDisabled || isSubmitButtonLoading" style="gap: 5px" @click="save">
                            <div v-if="isSubmitButtonLoading" class="spinner-border spinner-border-sm" role="status"></div>
                            <span>{{ $t('views.inventory.modals.expenses.save') }}</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { Modal } from 'bootstrap'
import VueDatePicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'
import CurrencyInput from '@/components/CurrencyInput.vue'
import InfoTooltip from '@/components/Variations/InfoTooltip.vue'
import { countryInfo } from '@/utils/helper'

const countries = countryInfo()

export default {
    name: 'ExpensesModal',
    props: {
        modelValue: {
            type: Boolean,
            default: false,
        },
        inventoryId: {
            type: [Number, Object],
            default: null,
        },
        asin: {
            type: String,
        },
    },
    components: {
        VueDatePicker,
        CurrencyInput,
        InfoTooltip,
    },
    data() {
        return {
            modalID: 'ExpensesModal-' + crypto.randomUUID(),
            modalInstance: null,
            expenses: [],
            platforms: ['Amazon', 'Walmart', 'Ebay', 'Target', 'Bestbuy', 'Other'],
            isSubmitButtonLoading: false,
        }
    },
    computed: {
        ...mapGetters('market', ['markets', 'currentMarket']),
        isNewRowButtonDisabled() {
            if (this.expenses.length === 1 && this.expenses[0].price != null) {
                return false
            }

            return this.expenses.some((row) => !row.amount || row.price == null)
        },
        getShortFromMarket() {
            const currentMarketShort = this.markets.find((market) => market.short === this.currentMarket.short)
            return currentMarketShort.sources.map((market) => market.source)
        },
    },
    methods: {
        ...mapActions('inventory', ['getExpenses', 'createExpenses', 'getCurrentCalculate']),
        ...mapMutations('inventory', ['SET_DETAIL']),
        closeModal() {
            this.modalInstance.hide()
            this.$emit('update:modelValue', false)
        },
        add() {
            this.expenses.push({ amount: null, price: null, end_date: null, buying_platform: null, buying_order_id: null, source: null })
        },
        remove(id) {
            if (confirm(this.$t('views.inventory.modals.expenses.removeConfirm'))) {
                this.expenses.splice(id, 1)
            }
        },
        save() {
            this.isSubmitButtonLoading = true
            if (this.expenses.length === 0) {
                this.expenses = [{ amount: null, price: null, end_date: null, buying_platform: null, buying_order_id: null, source: null }]
            }

            if (this.expenses.length > 0) {
                this.createExpenses({ id: this.inventoryId, payload: this.expenses }).finally(() => {
                    this.isSubmitButtonLoading = false
                    this.closeModal()
                })
            }
        },
        formatNumber(number) {
            if (number === null || number === undefined) return null

            const factor = Math.pow(10, 2)
            const rounded = Math.floor(number * factor + 1e-9) / factor
            return rounded.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            })
        },
        changeTargetCountry(source, index) {
            this.expenses[index].source = source
            this.selectedTargetCountry = source
            this.getCurrentCalculate({ source }).then(({ data }) => {
                this.expenses[index].price = data.total
                this.expenses[index].currency = data.currency
            })
        },
        handleOrderId(event, index) {
            const value = event.target.value.toString().toUpperCase()
            const regex = /^[A-Z0-9-]*$/
            const hasInvalidDoubleDash = value.includes('--')

            if (regex.test(value) && !hasInvalidDoubleDash) {
                this.expenses[index].buying_order_id = value
            } else {
                event.target.value = this.expenses[index].buying_order_id
            }
        },
    },
    watch: {
        modelValue: function (data) {
            if (data) {
                const modalEl = document.getElementById(this.modalID)
                this.modalInstance = new Modal(modalEl, { backdrop: true })
                this.modalInstance.show()
                modalEl.addEventListener('hidden.bs.modal', () => {
                    this.closeModal()
                })
            } else {
                this.closeModal()
            }
        },
        inventoryId: function (data) {
            if (data !== null) {
                this.getExpenses(this.inventoryId).then(({ data }) => {
                    this.expenses = data.map((row) => ({
                        amount: +row.amount,
                        price: +this.formatNumber(row.price),
                        end_date: row.end_date,
                        buying_platform: row.buying_platform,
                        buying_order_id: row.buying_order_id,
                        source: row.source,
                        currency: countries[row.source]?.symbol,
                    }))
                    if (!data.length > 0) {
                        this.add()
                    }
                })
            }
        },
    },
}
</script>

<style lang="scss">
.dp__theme_light {
    --dp-primary-color: #ffb53e;
}
.custom-form-control {
    max-height: 37px;
    font-size: 12px;
}
[id^='ExpensesModal-'] {
    .modal-body {
        padding-bottom: 0 !important;
    }
    @media (width <= 767px) {
        .modal-body {
            overflow-x: auto !important;

            &::-webkit-scrollbar {
                height: 4px;
            }
        }
        .modal-footer {
            gap: 1rem !important;
        }
    }
    .modal-footer {
        border-top: none !important;
    }
    .close-button {
        width: 35px;
        height: 35px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;

        &:hover {
            background-color: #f8fafd;
        }
    }
    .modal-table {
        margin-bottom: 0 !important;
        overflow-x: auto !important;

        tbody {
            tr {
                border-bottom: 1px solid #e7eaf3b2;

                td {
                    height: 67px;
                    vertical-align: middle;

                    &:first-child {
                        border-right: 1px solid #e7eaf3b2;
                    }
                }
            }
        }
        .dp__input_wrap {
            width: auto;
            > input {
                padding-inline-end: 12px !important;
                font-size: 14px;
                border: 0.6px solid #e7eaf3;
                color: #677788;
                width: 125px !important;
            }
        }
    }
    input::placeholder,
    select {
        font-size: 12px;
    }
}
</style>
